import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import BRAND from '../config/brand';
import { SERVICES_PATH, buildIntakePath } from '../config/planningOffers';
import { trackEvent, trackLinkClick, trackServicesNavClick } from '../lib/analytics';
import PublicSiteNav from './PublicSiteNav';
import ServiceNextStep from './ServiceNextStep';
import './PublicLinksPage.css';
import './HousingDecisionCalculator.css';

const CALCULATORS = [
  {
    id: 'car_lease_buy_calculator',
    href: '/calculators/car-lease-vs-buy',
    label: 'Car Lease vs Buy Calculator',
    description: 'Compare payments, dealer fees, interest, vehicle equity, and the estimated break-even year.',
  },
  {
    id: 'housing_decision_calculator',
    href: '/calculators/rent-vs-buy',
    label: 'Rent vs Buy Housing Calculator',
    description:
      'Compare buying, renting and investing, down payment choices, and extra mortgage payments.',
  },
  {
    id: 'paycheck_tax_estimator',
    href: '/paycheck-tax-estimator',
    label: 'Tax Readiness Check',
    description:
      'See whether your paycheck withholding is keeping up with your household tax complexity.',
  },
  {
    id: 'retirement_gap_estimator',
    href: '/retirement-gap-estimator',
    label: 'Retirement Gap Estimator',
    description: 'See whether your current savings pace is aligned with your retirement goal.',
  },
  {
    id: 'social_security_estimator',
    href: '/social-security-estimator',
    label: 'Social Security Claiming Estimator',
    description: 'Compare claiming ages and estimate lifetime benefit tradeoffs.',
  },
  {
    id: 'roth_vs_traditional_calculator',
    href: '/roth-vs-traditional-calculator',
    label: 'Roth vs. Traditional IRA Calculator',
    description:
      'Compare tax now vs. tax later, including a simple retirement withdrawal and Social Security view.',
  },
  {
    id: 'investment_performance_explorer',
    href: '/investment-performance-explorer',
    label: 'Investment Performance Explorer',
    description:
      'Compare historical asset-class returns and trailing performance using educational market proxies.',
  },
];

export default function CalculatorsIndexPage() {
  useEffect(() => {
    document.title = 'Planning Calculators | BPA';
    trackEvent('calculators_index_view', { page_path: '/calculators' });
  }, []);

  const planPath = buildIntakePath({
    offerId: 'comprehensive_plan',
    cta: 'get_financial_plan',
    contentCategory: 'general',
    promotionalAngle: 'calculators_index',
  });
  const servicesPath = `${SERVICES_PATH}#service-guide`;

  return (
    <div className="public-linktree-page">
      <PublicSiteNav />
      <main className="public-linktree-shell">
        <header className="public-linktree-header">
          <Link className="public-linktree-avatar" to="/links" aria-label="Back to links">
            <img src={BRAND.logoSrc} alt="" />
          </Link>
          <p className="hdc-brand-path">
            Clear Thinking → Better Decisions → The Golden Path
          </p>
          <h1>Free Planning Calculators</h1>
          <p>
            Start with the calculator that matches the decision you are actually trying to make —
            then get a comprehensive financial plan when you want to connect the result to your full household picture.
          </p>
        </header>

        <nav className="public-linktree-buttons" aria-label="Planning calculators">
          <section className="public-linktree-section" aria-labelledby="calculators-heading">
            <h2 id="calculators-heading">Calculators</h2>
            <div className="public-linktree-section-list">
              {CALCULATORS.map((item) => (
                <Link
                  key={item.id}
                  className="public-linktree-button public-linktree-button--calculator"
                  to={item.href}
                  data-button-id={item.id}
                  onClick={() =>
                    trackLinkClick(item.href, item.label, {
                      button_id: item.id,
                      button_label: item.label,
                      destination_type: 'internal',
                      calculator_id: item.id,
                      content_category: 'general',
                    })
                  }
                >
                  <span className="public-linktree-copy">
                    <span className="public-linktree-label">{item.label}</span>
                    <span className="public-linktree-description">{item.description}</span>
                  </span>
                </Link>
              ))}
            </div>
          </section>

          <Link
            className="public-linktree-button public-linktree-button--service"
            to={servicesPath}
            data-button-id="calculators_index_services_pricing"
            onClick={() =>
              trackServicesNavClick(servicesPath, 'Services & Pricing', {
                button_id: 'calculators_index_services_pricing',
                button_label: 'Services & Pricing',
                content_category: 'general',
                promotional_angle: 'calculators_index',
              })
            }
          >
            <span className="public-linktree-copy">
              <span className="public-linktree-label">Services &amp; Pricing</span>
              <span className="public-linktree-description">
                See the comprehensive financial plan and optional ongoing financial planning support.
              </span>
            </span>
          </Link>

          <Link
            className="public-linktree-button public-linktree-button--primary"
            to={planPath}
            data-button-id="calculators_index_financial_plan"
            onClick={() =>
              trackLinkClick(planPath, 'Get a Financial Plan', {
                button_id: 'calculators_index_financial_plan',
                button_label: 'Get a Financial Plan',
                destination_type: 'contact_form',
                content_category: 'general',
                promotional_angle: 'calculators_index',
              })
            }
          >
            <span className="public-linktree-copy">
              <span className="public-linktree-label">Get a Financial Plan</span>
              <span className="public-linktree-description">
                Want help deciding how a calculator result fits your full financial plan?
              </span>
            </span>
          </Link>

          <ServiceNextStep contentCategory="general" promotionalAngle="calculators_index" />
        </nav>
      </main>
    </div>
  );
}
